import {
  AitLocationBodyCell,
  AitLocationBodyRowGroup,
  AitLocationHeaderCell,
  AitTableActionUpdateComments,
  AitTablePosition,
  AitTableState,
} from "./interface";

/**
 * Apply a comments update action to the table state.
 *
 * Depending on the `action.location.position`, this will update the table
 * level comments, header comments, a specific header cell's comments, a
 * row-group's comments, or a specific body cell's comments.
 *
 * @template T - The cell payload type
 * @param newState - The current table state to update (modified in place)
 * @param action - The `AitTableActionUpdateComments` action describing the location and comment text
 * @returns The mutated `AitTableState<T>` with the comment applied
 */
export const trUpdateComments = <T>(
  newState: AitTableState<T>,
  action: AitTableActionUpdateComments<T>,
): AitTableState<T> => {
  switch (action.location.position) {
    case AitTablePosition.table: {
      // Update the comments in the tableData
      newState.originalTableData = {
        ...newState.originalTableData,
        comments: action.comment,
      };
      break;
    }
    case AitTablePosition.header: {
      // Update the comments in the headerData
      /* istanbul ignore else */
      if (newState.originalTableData.headerData) {
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: {
            ...newState.originalTableData.headerData,
            comments: action.comment,
          },
        };
      }
      break;
    }
    case AitTablePosition.headerCell: {
      // Update the comments in the headerData
      /* istanbul ignore else */
      if (newState.originalTableData.headerData) {
        const location = action.location as AitLocationHeaderCell;
        newState.originalTableData = {
          ...newState.originalTableData,
          headerData: {
            ...newState.originalTableData.headerData,
            rows: newState.originalTableData.headerData.rows.map((row) => ({
              ...row,
              cells: row.cells.map((c) =>
                c.aitid === location.cellId
                  ? {
                      ...c,
                      comments: action.comment,
                    }
                  : c,
              ),
            })),
          },
        };
      }
      break;
    }
    case AitTablePosition.bodyRowGroup: {
      // Update the comments in the bodyData
      const location = action.location as AitLocationBodyRowGroup;
      newState.originalTableData = {
        ...newState.originalTableData,
        bodyData: newState.originalTableData.bodyData.map((rowGroup) =>
          rowGroup.aitid === location.rowGroupId
            ? {
                ...rowGroup,
                comments: action.comment,
              }
            : rowGroup,
        ),
      };
      break;
    }
    case AitTablePosition.bodyCell: {
      // Update the comments in the bodyData
      const location = action.location as AitLocationBodyCell;
      newState.originalTableData = {
        ...newState.originalTableData,
        bodyData: newState.originalTableData.bodyData.map((rowGroup) =>
          rowGroup.aitid === location.rowGroupId
            ? {
                ...rowGroup,
                rows: rowGroup.rows.map((row) =>
                  row.aitid === location.rowId
                    ? {
                        ...row,
                        cells: row.cells.map((c) =>
                          c.aitid === location.cellId
                            ? {
                                ...c,
                                comments: action.comment,
                              }
                            : c,
                        ),
                      }
                    : row,
                ),
              }
            : rowGroup,
        ),
      };
      break;
    }
  }
  return newState;
};
